import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Iter 82 (UI hardening): catches any unhandled React render error in the
 * subtree, surfaces a friendly PT-BR fallback ("Algo deu errado") with two
 * CTAs — "Tentar novamente" (reset boundary) and "Voltar ao Painel" — and
 * logs the underlying error to the console for the team. Prevents white
 * pages when downstream components (e.g. SystemHealthPage with a partial
 * /api/system/health response) throw during render.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.assign('/dashboard');
  };

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'Erro inesperado ao renderizar a página.';
      return (
        <div
          className="min-h-[60vh] flex items-center justify-center p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full bg-white rounded-2xl shadow-md border border-slate-200 p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">
              Algo deu errado nesta tela
            </h2>
            <p className="text-sm text-slate-500 mt-1.5">
              Não foi possível renderizar esta página agora. Tente novamente — se o problema persistir, volte ao painel.
            </p>
            <p
              className="text-xs text-slate-400 mt-3 break-words font-mono bg-slate-50 rounded-md px-3 py-2"
              data-testid="error-boundary-message"
            >
              {message}
            </p>
            <div className="flex gap-2 justify-center mt-4">
              <Button
                onClick={this.handleReset}
                size="sm"
                className="bg-[#27272A] hover:bg-[#18181B] text-white"
                data-testid="error-boundary-retry"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Tentar novamente
              </Button>
              <Button
                onClick={this.handleGoHome}
                size="sm"
                variant="outline"
                data-testid="error-boundary-home"
              >
                <Home className="w-3.5 h-3.5 mr-1.5" />
                Voltar ao Painel
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
